(() => {
  'use strict';
  if (window.AvonticSetupPurchase) return;
  let posting = false;
  const get = (state, key) => state.dialog.querySelector(`[data-av-sp-${key}]`);
  const text = state => state.purchaseText;
  const modelOption = option => /size|model|device/i.test(option.name) && !/pack|keyboard/i.test(option.name);
  const colorOption = option => /colou?r|finish/i.test(option.name);
  function editableSelection(result, target) {
    if (result.blocked) return result;
    const questions = [...(result.questions || [])];
    const chosen = [...(result.chosen || [])];
    for (const option of target.options.filter(option => modelOption(option) || colorOption(option))) {
      const matched = chosen.find(item => item.position === option.position);
      const index = questions.findIndex(item => item.position === option.position);
      const existing = index >= 0 ? questions[index] : null;
      const question = { ...option, ...existing, kind: modelOption(option) ? 'model' : /keyboard/i.test(option.name) ? 'keyboard' : 'color', initialId: matched?.value.id || (option.values.length === 1 ? option.values[0].id : '') };
      if (colorOption(option)) question.values = option.values;
      if (index >= 0) questions[index] = question; else questions.push(question);
    }
    questions.sort((a, b) => Number(b.kind === 'model') - Number(a.kind === 'model') || a.position - b.position);
    return { ...result, chosen: chosen.filter(item => !questions.some(question => question.position === item.position)), questions };
  }
  const signature = state => state.purchaseApi.selection(state).map(value => value?.id).join(',');
  const status = (state, value, type = '') => {
    state.dialog.dataset.state = type;
    state.dialog.querySelector('[data-av-bundle-dialog-message]').textContent = value;
  };
  const button = state => state.dialog.querySelector('[data-av-bundle-continue]');
  const choicesKey = choices => choices.map(item => `${item.position}:${item.value.id}`).sort().join('|');
  const active = (state, pending) => state.dialog.open && state.pending === pending && state.dialog.isConnected;
  const money = (amount, currency) => new Intl.NumberFormat(document.documentElement.lang || 'en', { style: 'currency', currency }).format(amount / 100);
  const sourceReady = state => {
    const gate = state.scope.querySelector('[data-av-purchase-fit]');
    if (!state.source.options.some(modelOption)) return true;
    if (gate) return gate.dataset.avPurchaseFit === 'selected';
    const params = new URL(location.href).searchParams;
    return params.has('variant') || params.has('option_values');
  };
  function showImage(state, source, width, height, alt) {
    const image = get(state, 'image');
    image.alt = alt || '';
    if (source) {
      const expected = new URL(source, location.href);
      expected.searchParams.set('width', String(Math.min(Number(width) || 1600, 1600)));
      if (image.src === expected.href && image.complete && image.naturalWidth && !image.hidden) return;
    }
    const request = {};
    state.purchaseImageRequest = request;
    image.hidden = !source;
    image.style.visibility = 'hidden';
    image.onload = null;
    image.onerror = null;
    if (!source) { image.removeAttribute('srcset'); image.removeAttribute('src'); return; }
    const url = new URL(source, location.href);
    const maxWidth = Math.min(Number(width) || 1600, 1600);
    const widths = [...new Set([1000, 1254, 1600].map(value => Math.min(value, maxWidth)))];
    const sized = value => { const next = new URL(url); next.searchParams.set('width', String(value)); return next.href; };
    image.width = Number(width) || 800;
    image.height = Number(height) || 800;
    image.sizes = '(max-width: 700px) 275px, (max-width: 960px) calc(42vw - 67px), 336px';
    const reveal = async () => {
      try { await image.decode(); } catch (_) { return; }
      if (state.purchaseImageRequest === request) image.style.visibility = 'visible';
    };
    image.onload = reveal;
    image.onerror = () => { image.hidden = true; };
    image.srcset = widths.map(value => `${sized(value)} ${value}w`).join(', ');
    image.src = sized(maxWidth);
    if (image.complete && image.naturalWidth) reveal();
  }
  function quantity(state) {
    const input = get(state, 'quantity');
    return input.checkValidity() && Number.isSafeInteger(Number(input.value)) && Number(input.value) > 0 ? Number(input.value) : 0;
  }
  function cancel(state) {
    if (state.purchasePosting) return;
    state.purchaseRequest?.abort();
    state.purchaseRequest = null;
    if (state.purchase) {
      document.documentElement.classList.remove('av-setup-dialog-open');
      const trigger = state.purchaseCard;
      if (!state.dialog.open && state.purchaseReturnFocus !== false && trigger?.isConnected) requestAnimationFrame(() => { if (!state.dialog.open) trigger.focus({ preventScroll: true }); });
    }
  }
  function urlFor(state, chosen, target = state.pending.target) {
    return new URL(state.purchaseApi.buildUrl(target, chosen, state.pending.result.model, state.root.dataset.localeRoot, new URL(location.href)));
  }
  async function quote(url, handle, chosen, signal) {
    const endpoint = new URL(url);
    endpoint.searchParams.set('view', 'avontic-setup-quote');
    const response = await fetch(endpoint, { credentials: 'same-origin', cache: 'no-store', signal });
    if (!response.ok) throw new Error('request');
    const data = await response.json();
    const variant = data.variant;
    if (data.handle !== handle || !variant || !Number.isFinite(variant.price) || !/^\d+$/.test(variant.id) || !Array.isArray(variant.options) || !chosen.every(item => item.position < 1 || variant.options[item.position - 1] === item.value.name)) throw new Error('selection');
    return data;
  }
  function showPricing(state) {
    const pending = state.pending;
    const data = pending?.quote;
    const count = quantity(state);
    if (!data) return;
    get(state, 'total').textContent = count === 1 ? data.variant.formatted : count ? money(data.variant.price * count, data.currency) : '—';
    get(state, 'unit').hidden = count <= 1;
    get(state, 'unit').textContent = text(state).each.replace('__PRICE__', data.variant.formatted);
    const delta = get(state, 'delta');
    delta.textContent = '';
    const source = pending.sourceQuote;
    if (source && source.currency === data.currency && state.purchaseCard.dataset.avSetupPack !== 'true' && !state.source.handle.includes('teamready')) {
      const difference = data.variant.price - source.variant.price;
      delta.textContent = difference === 0 ? text(state).same : text(state)[difference > 0 ? 'upgrade' : 'downgrade'].replace('__PRICE__', money(Math.abs(difference), data.currency));
    }
    button(state).disabled = !count || !pending.canAdd || pending.uncertain || state.purchasePosting;
  }
  function presentQuote(state, data, chosen) {
    const pending = state.pending;
    pending.quote = data;
    pending.quotedKey = choicesKey(chosen);
    pending.canAdd = data.variant.available && !data.fitBlocked && !data.requiresSellingPlan;
    get(state, 'product').textContent = data.title;
    showImage(state, data.image, data.imageWidth, data.imageHeight, data.imageAlt || data.title);
    const input = get(state, 'quantity');
    input.min = String(data.variant.quantityMin || 1);
    input.step = String(data.variant.quantityStep || 1);
    if (data.variant.quantityMax) input.max = String(data.variant.quantityMax); else input.removeAttribute('max');
    if (!quantity(state)) input.value = input.min;
    const url = urlFor(state, chosen);
    url.searchParams.set('variant', data.variant.id);
    get(state, 'details').href = url.toString();
    showPricing(state);
    status(state, pending.canAdd ? text(state).ready : data.fitBlocked || data.requiresSellingPlan ? text(state).details_required : text(state).unavailable, pending.canAdd ? 'ready' : 'unavailable');
  }
  async function refresh(state) {
    const pending = state.pending;
    if (!pending || state.purchasePosting || pending.uncertain || !active(state, pending)) return;
    state.purchaseRequest?.abort();
    const controller = new AbortController();
    state.purchaseRequest = controller;
    const choices = state.purchaseApi.dialogChoices(state, true);
    pending.canAdd = false;
    pending.quote = null;
    button(state).disabled = true;
    get(state, 'retry').hidden = true;
    get(state, 'total').textContent = '—';
    get(state, 'delta').textContent = '';
    get(state, 'unit').hidden = true;
    const selected = [...choices.chosen].sort((a, b) => a.position - b.position).map(item => item.value.name.split('|')[0].trim());
    state.dialog.querySelector('[data-av-bundle-dialog-selection]').textContent = selected.join(' · ') || text(state).choose_model;
    const detailsUrl = urlFor(state, choices.chosen);
    if (!choices.complete) detailsUrl.searchParams.delete('option_values');
    get(state, 'details').href = detailsUrl.toString();
    if (!choices.complete || !choices.valid) {
      status(state, choices.valid ? text(state).choose : text(state).unavailable, choices.valid ? 'choose' : 'unavailable');
      return;
    }
    status(state, text(state).checking, 'loading');
    const timer = setTimeout(() => controller.abort(), 12000);
    try {
      const data = await quote(detailsUrl, pending.target.handle, choices.chosen, controller.signal);
      if (!active(state, pending) || controller.signal.aborted || state.purchaseRequest !== controller) return;
      if (pending.signature !== signature(state)) throw new Error('changed');
      presentQuote(state, data, choices.chosen);
    } catch (error) {
      if (!active(state, pending) || state.purchaseRequest !== controller) return;
      const unavailable = error.message === 'selection';
      status(state, unavailable ? text(state).unavailable : error.message === 'changed' ? text(state).source_changed : text(state).retry, unavailable ? 'unavailable' : 'error');
      get(state, 'retry').hidden = unavailable;
    } finally {
      clearTimeout(timer);
    }
  }
  function open(state, card, api) {
    if (posting) return;
    cancel(state);
    state.purchaseApi = api;
    state.purchaseCard = card;
    state.purchaseReturnFocus = true;
    state.purchaseText = JSON.parse(state.dialog.querySelector('[data-av-sp-text]').textContent);
    const target = state.products.get(card.dataset.avBundleTarget);
    if (!target) return;
    let result = api.cardResolution(state, card);
    const ready = sourceReady(state);
    if (!ready) {
      const questions = target.options.filter(modelOption).map(option => ({ ...option, kind: 'model' }));
      if (target.handle === 'ipad-a16-11-inch-case-stylus-bundle-avontic-essential-duo') questions.push({ position: -1, name: state.text.modelLabel, kind: 'model', values: [{ id: 'duo-a16', name: 'iPad A16 11-inch (2025)' }] });
      if (questions.length) {
        const positions = questions.map(q => q.position);
        const previousQuestions = result.questions || target.options.filter(option => !modelOption(option)).map(option => ({ ...option }));
        result = { blocked: false, model: '', chosen: (result.chosen || []).filter(item => !positions.includes(item.position)), questions: [...questions, ...previousQuestions.filter(item => !positions.includes(item.position))] };
      }
    }
    result = editableSelection(result, target);
    state.pending = { result, target, changes: [], signature: signature(state), quote: null, canAdd: false };
    const pending = state.pending;
    const dialog = state.dialog;
    dialog.querySelector('[data-av-bundle-dialog-title]').textContent = card.querySelector('.av-ibo__name')?.textContent.trim() || state.text.chooseOption;
    get(state, 'family').textContent = state.root.querySelector('.av-sc__family')?.textContent.trim() || text(state).eyebrow;
    get(state, 'product').textContent = '';
    showImage(state, card.dataset.avSetupImage, card.dataset.avSetupImageWidth, card.dataset.avSetupImageHeight, card.dataset.avSetupImageAlt);
    const list = get(state, 'included');
    list.replaceChildren();
    const included = card.querySelector('.av-sc__contents')?.textContent.trim() || '';
    included.split(/\s+\+\s+/).filter(Boolean).forEach(item => { const li = document.createElement('li'); li.textContent = item; list.append(li); });
    get(state, 'quantity').value = '1';
    get(state, 'quantity').min = '1';
    get(state, 'quantity').step = '1';
    get(state, 'quantity').removeAttribute('max');
    get(state, 'quantity').disabled = false;
    get(state, 'note').textContent = card.dataset.avSetupPack === 'true' ? text(state).pack_note : text(state).one_setup;
    get(state, 'button').textContent = card.dataset.avSetupBase === 'true' ? text(state).add_case : text(state).add;
    get(state, 'cart').hidden = true;
    get(state, 'retry').hidden = true;
    get(state, 'total').textContent = '—';
    get(state, 'delta').textContent = '';
    get(state, 'unit').hidden = true;
    get(state, 'details').href = target.url;
    get(state, 'details').inert = false;
    button(state).disabled = true;
    button(state).hidden = false;
    button(state).removeAttribute('aria-busy');
    dialog.querySelectorAll('[data-av-bundle-cancel]').forEach(b => { b.disabled = false; });
    const container = dialog.querySelector('[data-av-bundle-dialog-options]');
    container.replaceChildren();
    dialog.querySelector('[data-av-bundle-dialog-selection]').textContent = '';
    for (const [index, question] of (result.questions || []).entries()) {
      const label = document.createElement('label');
      label.textContent = question.kind === 'model' ? state.text.modelLabel : question.name || state.text.colorLabel;
      const select = document.createElement('select');
      select.setAttribute('aria-label', label.textContent);
      select.required = true;
      select.dataset.questionIndex = String(index);
      select.append(new Option(state.text.placeholder, ''));
      question.values.forEach(value => select.append(new Option(value.name.split('|')[0].trim(), String(value.id))));
      if (question.initialId) select.value = String(question.initialId);
      label.append(select); container.append(label);
    }
    container.onchange = event => {
      const question = result.questions[Number(event.target.dataset.questionIndex)];
      if (question?.kind === 'model') pending.result.model = '';
      refresh(state);
    };
    get(state, 'retry').onclick = () => refresh(state);
    get(state, 'quantity').oninput = () => { showPricing(state); if (!quantity(state)) status(state, text(state).quantity_invalid, 'error'); else if (pending.canAdd) status(state, text(state).ready, 'ready'); };
    dialog.showModal();
    document.documentElement.classList.add('av-setup-dialog-open');
    dialog.querySelector('[data-av-bundle-dialog-title]').focus({ preventScroll: true });
    if (result.blocked) {
      status(state, state.text[result.reason] || text(state).unavailable, 'unavailable');
      get(state, 'quantity').disabled = true;
      return;
    }
    refresh(state);
    if (ready) {
      const selected = api.selection(state);
      const chosen = selected.map((value, index) => ({ position: state.source.options[index].position, value }));
      if (chosen.every(item => item.value)) {
        const sourceUrl = api.buildUrl(state.source, chosen, result.model, state.root.dataset.localeRoot, new URL(location.href));
        quote(sourceUrl, state.source.handle, chosen, AbortSignal.timeout(10000)).then(data => {
          if (active(state, pending)) { pending.sourceQuote = data; showPricing(state); }
        }).catch(() => {});
      }
    }
  }
  async function submit(state, event) {
    if (event.submitter !== button(state) || button(state).disabled || posting) return;
    const pending = state.pending;
    if (!pending || !active(state, pending) || !pending.canAdd || pending.uncertain) return;
    if (pending.signature !== signature(state)) { status(state, text(state).source_changed, 'error'); button(state).disabled = true; return; }
    const choices = state.purchaseApi.dialogChoices(state);
    const count = quantity(state);
    if (!choices.complete || !choices.valid || !count || pending.quotedKey !== choicesKey(choices.chosen)) return;
    posting = true; state.purchasePosting = true;
    button(state).disabled = true;
    button(state).setAttribute('aria-busy', 'true');
    get(state, 'button').textContent = text(state).adding;
    state.dialog.querySelectorAll('select,input,[data-av-bundle-cancel]').forEach(el => { el.disabled = true; });
    get(state, 'details').inert = true;
    let sent = false;
    let confirmed = false;
    let rejected = false;
    try {
      const data = await quote(urlFor(state, choices.chosen), pending.target.handle, choices.chosen, AbortSignal.timeout(12000));
      if (!active(state, pending) || pending.signature !== signature(state)) throw new Error('changed');
      if (!data.variant.available || data.fitBlocked || data.requiresSellingPlan) { presentQuote(state, data, choices.chosen); return; }
      if (data.variant.id !== pending.quote.variant.id || data.variant.price !== pending.quote.variant.price || data.currency !== pending.quote.currency) {
        presentQuote(state, data, choices.chosen); status(state, text(state).changed, 'review'); return;
      }
      const root = state.root.dataset.localeRoot || '/';
      const sections = [];
      document.documentElement.dispatchEvent(new CustomEvent('cart:prepare-bundled-sections', { bubbles: true, detail: { sections } }));
      const targetUrl = urlFor(state, choices.chosen);
      targetUrl.searchParams.set('variant', data.variant.id);
      sent = true;
      const response = await fetch(`${root}cart/add.js`, { method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' }, body: JSON.stringify({ items: [{ id: data.variant.id, quantity: count }], sections: [...new Set(sections)].slice(0,5), sections_url: targetUrl.pathname + targetUrl.search }), signal: AbortSignal.timeout(25000) });
      const added = await response.json();
      if (!response.ok) {
        rejected = true;
        throw new Error(typeof added.description === 'string' ? added.description : text(state).unavailable);
      }
      confirmed = true; pending.canAdd = false;
      get(state, 'button').textContent = text(state).added;
      get(state, 'cart').hidden = false;
      status(state, text(state).added, 'success');
      const cartResponse = await fetch(`${root}cart.js`, { credentials: 'same-origin', cache: 'no-store', signal: AbortSignal.timeout(12000) });
      if (!cartResponse.ok) throw new Error('cart');
      const cart = await cartResponse.json();
      cart.sections = added.sections || {};
      const drawer = document.querySelector('cart-drawer');
      const section = drawer?.closest('[id^="shopify-section-"]')?.id.replace('shopify-section-', '');
      if (section && !cart.sections[section]) {
        const refreshUrl = new URL(targetUrl); refreshUrl.searchParams.set('section_id', section);
        const sectionResponse = await fetch(refreshUrl, { credentials: 'same-origin', signal: AbortSignal.timeout(10000) });
        if (!sectionResponse.ok) throw new Error('cart');
        cart.sections[section] = await sectionResponse.text();
      }
      document.documentElement.dispatchEvent(new CustomEvent('variant:add', { bubbles: true, detail: { items: added.items || [added], cart, onSuccessDo: 'force_open_drawer' } }));
      if (drawer && section) {
        state.purchaseReturnFocus = false;
        state.dialog.close();
        document.documentElement.classList.remove('av-setup-dialog-open');
      }
      document.documentElement.dispatchEvent(new CustomEvent('cart:change', { bubbles: true, detail: { baseEvent: 'variant:add', onSuccessDo: 'force_open_drawer', cart } }));
    } catch (error) {
      if (confirmed) {
        status(state, text(state).added, 'success');
        document.dispatchEvent(new CustomEvent('cart:refresh'));
      } else if (sent && !rejected) {
        pending.uncertain = true; pending.canAdd = false;
        get(state, 'cart').hidden = false;
        status(state, text(state).uncertain, 'error');
      } else {
        status(state, rejected ? error.message : error.message === 'changed' ? text(state).source_changed : text(state).retry, 'error');
      }
    } finally {
      posting = false; state.purchasePosting = false;
      button(state).removeAttribute('aria-busy');
      if (!confirmed) get(state, 'button').textContent = state.purchaseCard.dataset.avSetupBase === 'true' ? text(state).add_case : text(state).add;
      state.dialog.querySelectorAll('select,input,[data-av-bundle-cancel]').forEach(el => { el.disabled = false; });
      get(state, 'details').inert = false;
      if (confirmed || pending.uncertain) {
        button(state).disabled = true;
        state.dialog.querySelectorAll('select,input').forEach(el => { el.disabled = true; });
      } else showPricing(state);
      if (!state.dialog.open) cancel(state);
    }
  }
  window.AvonticSetupPurchase = Object.freeze({ open, submit, cancel });
})();
